import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import PostHogProvider from './providers/PostHogProvider';
import ProtectedRoute from './components/ProtectedRoute';
import CookieConsent from './components/CookieConsent';
import DashboardLayout from './components/DashboardLayout';

// Retry wrapper for lazy imports — handles stale chunks after deployments.
// When a chunk 404s (new deploy changed hashes), trigger a one-time full reload
// so the browser fetches fresh HTML with the new chunk names. The sessionStorage
// flag prevents an infinite reload loop if the second attempt also fails.
const CHUNK_RELOAD_KEY = 'chunk-reload';

function lazyWithRetry(importFn: () => Promise<{ default: React.ComponentType }>) {
  return lazy(() =>
    importFn()
      .then((module) => {
        // Successful load — clear any prior reload flag so future failures can retry
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        return module;
      })
      .catch((error) => {
        if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
          window.location.reload();
          // Return a never-resolving promise so React doesn't render an error
          // state in the brief window before the browser navigates
          return new Promise<{ default: React.ComponentType }>(() => {});
        }
        // Already reloaded once and still failing — let ErrorBoundary handle it
        throw error;
      })
  );
}

// Lazy-loaded page components for code splitting
const LoginPage = lazyWithRetry(() => import('./pages/LoginPage'));
const RegisterPage = lazyWithRetry(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'));
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage'));
const ProfileSettingsPage = lazyWithRetry(() => import('./pages/ProfileSettingsPage'));
const AccountSettingsPage = lazyWithRetry(() => import('./pages/AccountSettingsPage'));
const LibraryPage = lazyWithRetry(() => import('./pages/LibraryPage'));
const ReadingPlanPage = lazyWithRetry(() => import('./pages/ReadingPlanPage'));
const DailySessionPage = lazyWithRetry(() => import('./pages/DailySessionPage'));
const TermsPage = lazyWithRetry(() => import('./pages/TermsPage'));
const PrivacyPage = lazyWithRetry(() => import('./pages/PrivacyPage'));
const LandingPage = lazyWithRetry(() => import('./pages/LandingPage'));
const PublicLayout = lazyWithRetry(() => import('./components/PublicLayout'));
const AdminApp = lazy(() => import('./admin/AdminApp').then(m => ({ default: m.AdminApp })));

const App = () => (
  <AuthProvider>
    <BrowserRouter>
      <PostHogProvider>
      <Suspense fallback={null}>
      <Routes>
        {/* Anonymous routes (PublicLayout: top nav with Log in + Sign up) */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
        </Route>

        {/* Protected routes with dashboard layout */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
        </Route>

        {/* Settings route */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ProfileSettingsPage />} />
          <Route path="account" element={<AccountSettingsPage />} />
        </Route>

        {/* Library route */}
        <Route
          path="/library"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<LibraryPage />} />
          <Route path=":sourceDocId/plan/new" element={<ReadingPlanPage />} />
        </Route>

        {/* Reading plan view route */}
        <Route
          path="/reading-plans"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path=":id" element={<ReadingPlanPage />} />
          <Route path=":planId/sessions/:sessionId" element={<DailySessionPage />} />
        </Route>

        {/* Admin routes */}
        <Route path="/admin/*" element={<AdminApp />} />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      <CookieConsent />
      </PostHogProvider>
    </BrowserRouter>
  </AuthProvider>
);

export default App;
